/**
 * The Artwork step's deliberate steps, each in a dialog (the journeys branch's
 * round 18 mock-up, changed by docs 39, 62 and 63: a proof is always sent, the
 * customer decides, and staff decide only for them):
 * - Upload: a file for each part the customer owes, or a replacement for one
 *   they gave. Each file is checked as it arrives; a part another version of
 *   the line already has can be shared instead of supplied again.
 * - Decide for the customer: approve (ticking every point that is worth
 *   knowing, and saying how they approved it) or send the proof back (the
 *   reason and what to change), as the customer said by phone or in person.
 * - Reopen: undoes an approval before release; the proof awaits them again.
 * - Resend proof: the proof goes to the customer's contact again.
 * - Check warnings: a check the classification does not know yet is held;
 *   prepress tiers it.
 * - Accept for the customer: a `check_without_proof` file's worth-knowing
 *   points, accepted for them.
 *
 * `onSubmit` resolves to the refusal to show, or null once done; a refusal on
 * one input shows beside it. Every sentence is the read's.
 */
import { useId, useRef, useState } from "react";
import type { PendingCombinedFile } from "@shared-core/data/api-types";
import type {
  ArtworkCommand,
  ArtworkFileChoice,
  FindingTier,
  OrderArtworkView,
  QuoteArtworkCommand,
  StaffArtworkLine,
  StaffFinding,
  StaffProof,
  StaffProofDecisionRequest,
  StaffVersionArtwork,
} from "@shared-core/data/artwork-staff-types";
import { findVersion } from "@shared-core/data/artwork-staff-actions";
import {
  shareOffers,
  versionTitle,
  worthKnowing,
} from "@shared-core/domain/artwork-staff";
import type { OrderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";
import { FailureBanner, Field } from "../orders/OrderDialogs";
import { PageTaggingStep } from "./PageTagger";

/** The version a dialog acts on, with its line. */
export interface ArtworkTarget {
  line: StaffArtworkLine;
  /** The line's title ("Window posters"): a version's name when it has none. */
  lineName: string;
  version: StaffVersionArtwork;
}

type Submit = (command: ArtworkCommand) => Promise<OrderStepFailure | null>;

/** ".01 B Bath", or ".02 Pull-up banner" for a line with no versions. */
export function targetName(target: ArtworkTarget): string {
  return versionTitle(target.line, target.version, target.lineName);
}

/** What each dialog is opened for. The page holds it, so the action box opens the same dialogs. */
export type ArtworkDialogRequest =
  | { kind: "upload"; versionId: string; replacing: boolean }
  | { kind: "decide_proof"; versionId: string }
  | { kind: "reopen"; versionId: string }
  | { kind: "resend"; versionId: string }
  | { kind: "check_warnings"; versionId: string }
  | { kind: "accept"; versionId: string };

function DialogButtons({
  busy,
  label,
  onClose,
}: {
  busy: boolean;
  label: string;
  onClose: () => void;
}) {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" disabled={busy}>
        {label}
      </Button>
    </DialogFooter>
  );
}

const TIER_LABELS: Record<FindingTier, string> = {
  cant_print: "Can't print",
  worth_knowing: "Worth knowing",
  we_will_sort: "We'll sort it",
  unrecognised: "Not known yet",
};

// ── Upload files, or replace one ─────────────────────────────────────────

/**
 * The per-part upload. The order's Artwork step wraps it in `UploadFilesDialog`, which adds the one file for every
 * part; the quote page opens it as it is (a quote takes no one file, doc 87 section 16).
 */
export function UploadFilesFormDialog({
  target,
  replacing,
  busy,
  onClose,
  onSubmit,
  onUploadWhole,
}: {
  target: ArtworkTarget;
  /** Opened from "Replace a part": the parts that already have a file are the ones to choose. */
  replacing: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (command: QuoteArtworkCommand) => Promise<OrderStepFailure | null>;
  /** Sends one file for every part, offered above the parts where the read carries `combined_upload_url`. */
  onUploadWhole?: (file: File) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const { version } = target;
  // A part that accepts a file now; a first upload offers those with none, a replacement those that have one.
  const parts = version.parts.filter(
    (p) => p.upload_url !== null && (replacing ? p.file !== null : true),
  );
  const [chosen, setChosen] = useState<Record<string, File>>({});
  const offers = replacing ? [] : shareOffers(target.line, version);
  const [sharing, setSharing] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const isShared = (partKey: string) => partKey in sharing;
  const nothingChosen =
    Object.keys(chosen).length === 0 && Object.keys(sharing).length === 0;
  const typedProblem = nothingChosen
    ? replacing
      ? "Choose the file to replace."
      : "Choose a file."
    : undefined;
  const filesError =
    (touched ? typedProblem : undefined) ?? failure?.fieldErrors.file;
  const title = replacing
    ? `${parts.length > 1 ? "Replace a part" : "Replace the file"} · ${targetName(target)}`
    : version.parts.some((p) => p.file)
      ? `Upload a new file · ${targetName(target)}`
      : `Upload artwork · ${targetName(target)}`;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {replacing
              ? "A new file is checked as it arrives and replaces the old one. Any proof out for this version is replaced by a new one."
              : "Each file is checked as it arrives, and a proof follows once every part is in."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (typedProblem) return;
            const refused = await onSubmit({
              kind: "upload_files",
              versionId: version.version_id,
              files: parts.flatMap((part): ArtworkFileChoice[] => {
                const file = chosen[part.part_key];
                if (isShared(part.part_key)) {
                  return [
                    {
                      partKey: part.part_key,
                      shareFromVersionId: sharing[part.part_key]!,
                    },
                  ];
                }
                return file ? [{ partKey: part.part_key, file }] : [];
              }),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          {!replacing && onUploadWhole && version.combined_upload_url && (
            <WholeFileUpload busy={busy} onUpload={onUploadWhole} />
          )}
          <fieldset
            className="space-y-3"
            aria-describedby={filesError ? `${id}-files-error` : undefined}
          >
            <legend className="sr-only">Files</legend>
            {parts.map((part) => {
              const key = part.part_key;
              const offer = offers.find((o) => o.partKey === key);
              return (
                <div key={key} className="space-y-1">
                  <Label htmlFor={`${id}-${key}`}>{part.label ?? "File"}</Label>
                  {offer && (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isShared(key)}
                        onChange={(e) =>
                          setSharing((s) => {
                            const next = { ...s };
                            if (e.target.checked)
                              next[key] = offer.fromVersionId;
                            else delete next[key];
                            return next;
                          })
                        }
                      />
                      Share {offer.fromName}&apos;s{" "}
                      {(part.label ?? "file").toLowerCase()} ({offer.fileName})
                    </label>
                  )}
                  {!isShared(key) && (
                    <Input
                      id={`${id}-${key}`}
                      type="file"
                      accept="application/pdf,.pdf"
                      aria-invalid={
                        filesError && !chosen[key] ? true : undefined
                      }
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        setChosen((c) => {
                          const next = { ...c };
                          if (file) next[key] = file;
                          else delete next[key];
                          return next;
                        });
                      }}
                    />
                  )}
                  {part.file && !chosen[key] && (
                    <p className="text-xs text-muted-foreground">
                      Now {part.file.file_name}
                      {part.file.shared_with.length > 0 &&
                        `, shared with ${part.file.shared_with.join(", ")}: replacing it here makes a new file for this version only`}
                    </p>
                  )}
                </div>
              );
            })}
          </fieldset>
          {filesError && (
            <p id={`${id}-files-error`} className="text-sm text-red-600">
              {filesError}
            </p>
          )}
          <DialogButtons
            busy={busy}
            label={replacing ? "Replace" : "Upload"}
            onClose={onClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One file for every part (doc 87 §6), above the per-part slots: a PDF with every part's pages, kept for a person to say
 * which pages are which. Offered where the read carries the version's `combined_upload_url`; nothing is split yet.
 */
function WholeFileUpload({
  busy,
  onUpload,
}: {
  busy: boolean;
  onUpload: (file: File) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const message = failure?.fieldErrors.file ?? failure?.summary;
  return (
    <section
      aria-label="One file for every part"
      className="space-y-1 rounded-md border bg-muted/30 p-3"
    >
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={input}
          id={`${id}-whole`}
          type="file"
          accept="application/pdf,.pdf"
          className="sr-only"
          aria-label="PDF with every part"
          aria-invalid={message ? true : undefined}
          aria-describedby={message ? `${id}-whole-error` : undefined}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            setFailure(await onUpload(file));
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          Upload one file for every part
        </Button>
        <p className="text-xs text-muted-foreground">
          A PDF with the cover and text together. We&apos;ll split it.
        </p>
      </div>
      {message && (
        <p id={`${id}-whole-error`} className="text-sm text-red-600">
          {message}
        </p>
      )}
    </section>
  );
}

/**
 * Upload files, or (once a one-file upload waits for its tags) say which pages are which. The dialog stays open
 * across the upload: the page re-reads, the version carries the pending file, and this turns into the tagging step.
 */
export function UploadFilesDialog(props: {
  target: ArtworkTarget;
  replacing: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit;
}) {
  const pending = props.target.version.pending_combined_file ?? null;
  // The step it was showing, kept until the dialog closes: a split re-reads the version with no pending file first.
  const [shown, setShown] = useState<PendingCombinedFile | null>(
    props.replacing ? null : pending,
  );
  if (!props.replacing && pending && pending.id !== shown?.id) {
    setShown(pending);
  }
  if (!props.replacing && shown) {
    return <CombinedFileDialog {...props} pending={shown} />;
  }
  return (
    <UploadFilesFormDialog
      {...props}
      onUploadWhole={(file) =>
        props.onSubmit({
          kind: "upload_whole_file",
          versionId: props.target.version.version_id,
          file,
        })
      }
    />
  );
}

// ── Which pages are which? ───────────────────────────────────────────────

/** The one file for every part, with its page pictures: tag them, then split, or cancel the step. */
export function CombinedFileDialog({
  target,
  pending,
  busy,
  onClose,
  onSubmit,
}: {
  target: ArtworkTarget;
  pending: PendingCombinedFile;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit;
}) {
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const run = async (command: ArtworkCommand) => {
    const refused = await onSubmit(command);
    setFailure(refused);
    if (!refused) onClose();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Which pages are which?</DialogTitle>
          <DialogDescription>{targetName(target)}</DialogDescription>
        </DialogHeader>
        <PageTaggingStep
          key={pending.id}
          pending={pending}
          busy={busy}
          refusal={
            failure && {
              banner: failure.summary,
              pages: failure.fieldErrors["assignment.pages"] ?? null,
            }
          }
          onSplit={(request) =>
            void run({
              kind: "split_combined_file",
              versionId: target.version.version_id,
              input: request,
            })
          }
          onCancel={() =>
            void run({
              kind: "discard_combined_file",
              versionId: target.version.version_id,
            })
          }
        />
      </DialogContent>
    </Dialog>
  );
}

// ── Decide a proof for the customer ──────────────────────────────────────

/** Decide a version's current proof for the customer (the order's Artwork step). */
export function DecideForCustomerDialog({
  target,
  party,
  busy,
  onClose,
  onSubmit,
}: {
  target: ArtworkTarget;
  party: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit;
}) {
  const proof = target.version.current_proof;
  if (!proof) return null;
  return (
    <DecideProofDialog
      proof={proof}
      subject={targetName(target)}
      party={party}
      busy={busy}
      onClose={onClose}
      onDecide={(input) =>
        onSubmit({
          kind: "decide_proof",
          versionId: target.version.version_id,
          proofId: proof.id,
          input,
        })
      }
    />
  );
}

/**
 * Decide a proof for the customer: approve it, ticking every point worth
 * knowing and saying how they approved it, or send it back with a reason and
 * what to change. The order's Artwork step and Prepress home's *Item proofs to
 * approve* (a repeatable item's proof) both open it.
 */
export function DecideProofDialog({
  proof,
  subject,
  party,
  busy,
  onClose,
  onDecide,
}: {
  proof: StaffProof;
  /** What the proof is of: ".01 B Bath", "Patient leaflet PL12 Leeds". */
  subject: string;
  party: string;
  busy: boolean;
  onClose: () => void;
  onDecide: (
    input: StaffProofDecisionRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const worth = worthKnowing(proof.findings);
  const [outcome, setOutcome] = useState<"approved" | "rejected">("approved");
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [comment, setComment] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const problems: Partial<
    Record<"accepted_finding_ids" | "note" | "reason_code" | "comment", string>
  > =
    outcome === "approved"
      ? {
          ...(worth.some((f) => !ticked.has(f.id))
            ? {
                accepted_finding_ids:
                  "Tick each point to approve this proof as it is",
              }
            : {}),
          ...(note.trim() ? {} : { note: `Say how ${party} approved it.` }),
        }
      : {
          ...(reasonCode ? {} : { reason_code: "Choose why it is sent back" }),
          ...(comment.trim()
            ? comment.length > 2000
              ? { comment: "At most 2,000 characters" }
              : {}
            : { comment: "Say what needs changing" }),
        };
  const shown = (field: keyof typeof problems) =>
    (touched ? problems[field] : undefined) ?? failure?.fieldErrors[field];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Decide for {party} · {subject} {proof.label}
          </DialogTitle>
          <DialogDescription>
            {proof.label} · of Rev {proof.of_revision}, as {party} told us. It
            is recorded as decided by Pollards for {party}.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (Object.keys(problems).length > 0) return;
            const refused = await onDecide(
              outcome === "approved"
                ? {
                    outcome,
                    accepted_finding_ids: worth.map((f) => f.id),
                    note: note.trim(),
                    wording_version: proof.wording_version,
                  }
                : {
                    outcome,
                    reason_code: reasonCode,
                    comment: comment.trim(),
                    wording_version: proof.wording_version,
                  },
            );
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Decision</legend>
            <RadioGroup
              value={outcome}
              onValueChange={(v) => setOutcome(v as "approved" | "rejected")}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="approved" id={`${id}-approve`} />
                <Label htmlFor={`${id}-approve`} className="font-normal">
                  Approve
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="rejected" id={`${id}-reject`} />
                <Label htmlFor={`${id}-reject`} className="font-normal">
                  Send back
                </Label>
              </div>
            </RadioGroup>
          </fieldset>
          {outcome === "approved" ? (
            <>
              {worth.length > 0 && (
                <fieldset
                  className="space-y-1"
                  aria-describedby={
                    shown("accepted_finding_ids")
                      ? `${id}-ticks-error`
                      : undefined
                  }
                >
                  <legend className="text-sm font-medium">
                    Worth knowing: tick each point {party} accepted
                  </legend>
                  {worth.map((finding) => (
                    <label
                      key={finding.id}
                      className="flex items-start gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={ticked.has(finding.id)}
                        onChange={(e) =>
                          setTicked((t) => {
                            const next = new Set(t);
                            if (e.target.checked) next.add(finding.id);
                            else next.delete(finding.id);
                            return next;
                          })
                        }
                      />
                      <span>{finding.sentence}</span>
                    </label>
                  ))}
                  {shown("accepted_finding_ids") && (
                    <p
                      id={`${id}-ticks-error`}
                      className="text-sm text-red-600"
                    >
                      {shown("accepted_finding_ids")}
                    </p>
                  )}
                </fieldset>
              )}
              <Field
                id={`${id}-note`}
                label={`How ${party} approved it`}
                error={shown("note")}
                hint="Kept with the proof: “Approved by phone with Jo Smith”."
              >
                {(aria) => (
                  <Textarea
                    {...aria}
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                )}
              </Field>
            </>
          ) : (
            <>
              <Field
                id={`${id}-reason`}
                label="Why"
                error={shown("reason_code")}
              >
                {(aria) => (
                  <select
                    {...aria}
                    className="h-9 w-full rounded-md border border-border bg-card px-3 text-base sm:text-sm"
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value)}
                  >
                    <option value="">Choose a reason</option>
                    {proof.reason_options.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field
                id={`${id}-comment`}
                label="What needs changing"
                error={shown("comment")}
              >
                {(aria) => (
                  <Textarea
                    {...aria}
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                )}
              </Field>
            </>
          )}
          <DialogButtons
            busy={busy}
            label={outcome === "approved" ? "Record approval" : "Send it back"}
            onClose={onClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Reopen an approval ───────────────────────────────────────────────────

export function ReopenDialog({
  target,
  busy,
  onClose,
  onSubmit,
}: {
  target: ArtworkTarget;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit;
}) {
  const id = useId();
  const proof = target.version.current_proof;
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  if (!proof) return null;
  const typedProblem = reason.trim()
    ? reason.length > 500
      ? "At most 500 characters"
      : undefined
    : "Say why it is reopened.";
  const reasonError =
    (touched ? typedProblem : undefined) ?? failure?.fieldErrors.reason;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reopen · {targetName(target)}</DialogTitle>
          <DialogDescription>
            Undoes the approval of {proof.label}: it awaits the customer&apos;s
            decision again, on the same files (Rev {proof.of_revision}). To
            change a file, replace it instead.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (typedProblem) return;
            const refused = await onSubmit({
              kind: "reopen_proof",
              versionId: target.version.version_id,
              proofId: proof.id,
              input: { reason: reason.trim() },
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field id={`${id}-reason`} label="Why" error={reasonError}>
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={reason}
                placeholder="Customer wants the new opening hours"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogButtons busy={busy} label="Reopen" onClose={onClose} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Resend a proof ───────────────────────────────────────────────────────

export function ResendProofDialog({
  target,
  party,
  busy,
  onClose,
  onSubmit,
}: {
  target: ArtworkTarget;
  party: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit;
}) {
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const proof = target.version.current_proof;
  if (!proof) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Resend {proof.label} · {targetName(target)}
          </DialogTitle>
          <DialogDescription>
            {proof.label} · of Rev {proof.of_revision} goes to {party} again,
            now, with a fresh link. It keeps its number.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const refused = await onSubmit({
              kind: "resend_proof",
              versionId: target.version.version_id,
              proofId: proof.id,
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <DialogButtons busy={busy} label="Resend proof" onClose={onClose} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Check warnings: tier a held check ────────────────────────────────────

const DECIDABLE_TIERS: Array<{
  tier: Exclude<FindingTier, "unrecognised">;
  label: string;
}> = [
  { tier: "cant_print", label: TIER_LABELS.cant_print },
  { tier: "worth_knowing", label: TIER_LABELS.worth_knowing },
  { tier: "we_will_sort", label: TIER_LABELS.we_will_sort },
];

/** One held finding and its tier choice: a decision each, so a refusal is beside its own. */
function HeldFindingRow({
  finding,
  busy,
  onDecide,
}: {
  finding: StaffFinding;
  busy: boolean;
  onDecide: (tier: string) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [tier, setTier] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  return (
    <li className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">{finding.check_name}</p>
      {finding.detail && (
        <p className="text-xs text-muted-foreground">{finding.detail}</p>
      )}
      <FailureBanner failure={failure} />
      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor={`${id}-tier`} className="sr-only">
          Tier of {finding.check_name}
        </Label>
        <select
          id={`${id}-tier`}
          className="h-9 rounded-md border border-border bg-card px-3 text-base sm:text-sm"
          value={tier}
          onChange={(e) => setTier(e.target.value)}
        >
          <option value="">Choose what it means</option>
          {DECIDABLE_TIERS.map((t) => (
            <option key={t.tier} value={t.tier}>
              {t.label}
            </option>
          ))}
        </select>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy || !tier}
          aria-label={`Decide ${finding.check_name}`}
          onClick={async () => setFailure(await onDecide(tier))}
        >
          Decide
        </Button>
      </div>
    </li>
  );
}

export function CheckWarningsDialog({
  target,
  busy,
  onClose,
  onSubmit,
}: {
  target: ArtworkTarget;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit;
}) {
  const held = target.version.parts
    .flatMap((p) => p.findings)
    .filter((f) => f.tier === "unrecognised" && f.decide_url !== null);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Check warnings · {targetName(target)}</DialogTitle>
          <DialogDescription>
            The check found something the classification does not know yet. The
            customer sees only that we are taking a closer look. Say what each
            one means; the version carries on once every one is decided.
          </DialogDescription>
        </DialogHeader>
        {held.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Every check on this version is decided.
          </p>
        ) : (
          <ul className="space-y-2">
            {held.map((finding) => (
              <HeldFindingRow
                key={finding.id}
                finding={finding}
                busy={busy}
                onDecide={(tier) =>
                  onSubmit({
                    kind: "decide_finding",
                    versionId: target.version.version_id,
                    findingId: finding.id,
                    input: { tier },
                  })
                }
              />
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Accept a `check_without_proof` file for the customer ─────────────────

export function AcceptForCustomerDialog({
  target,
  party,
  busy,
  onClose,
  onSubmit,
}: {
  target: ArtworkTarget;
  party: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit;
}) {
  const id = useId();
  const findings = target.version.accept_without_proof_findings;
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const notTicked = findings.some((f) => !ticked.has(f.id));
  const noteProblem = note.trim() ? undefined : `Say how ${party} agreed.`;
  const ticksError =
    (touched && notTicked
      ? "Tick each point to accept this file as it is"
      : undefined) ?? failure?.fieldErrors.accepted_finding_ids;
  const noteError =
    (touched ? noteProblem : undefined) ?? failure?.fieldErrors.note;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Accept for {party} · {targetName(target)}
          </DialogTitle>
          <DialogDescription>
            This line is checked and goes ahead with no proof. Accepting is
            recorded as decided by Pollards for {party}.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (notTicked || noteProblem) return;
            const refused = await onSubmit({
              kind: "accept_without_proof",
              versionId: target.version.version_id,
              input: {
                accepted_finding_ids: findings.map((f) => f.id),
                note: note.trim(),
              },
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">
              Worth knowing: tick each point {party} accepted
            </legend>
            {findings.map((finding) => (
              <label
                key={finding.id}
                className="flex items-start gap-2 text-sm"
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={ticked.has(finding.id)}
                  onChange={(e) =>
                    setTicked((t) => {
                      const next = new Set(t);
                      if (e.target.checked) next.add(finding.id);
                      else next.delete(finding.id);
                      return next;
                    })
                  }
                />
                <span>{finding.sentence}</span>
              </label>
            ))}
            {ticksError && <p className="text-sm text-red-600">{ticksError}</p>}
          </fieldset>
          <Field
            id={`${id}-note`}
            label={`How ${party} agreed`}
            error={noteError}
            hint="Kept with the file: “Agreed by phone with Jo Smith”."
          >
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          <DialogButtons busy={busy} label="Accept" onClose={onClose} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── The page's one host ──────────────────────────────────────────────────

/**
 * The dialog the page has open, if any, for the version it names. The page
 * holds the request, so the Artwork step's buttons and the action box's rows
 * open the same dialogs. Closes itself when the version is no longer there.
 */
export function ArtworkDialogHost({
  artwork,
  lineNames,
  request,
  party,
  busy,
  onClose,
  onSubmit,
}: {
  artwork: OrderArtworkView;
  /** Line title by line number ("Window posters"). */
  lineNames: ReadonlyMap<string, string>;
  request: ArtworkDialogRequest | null;
  party: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit;
}) {
  if (!request) return null;
  const version = findVersion(artwork, request.versionId);
  const line = artwork.lines.find((l) =>
    l.versions.some((v) => v.version_id === request.versionId),
  );
  if (!version || !line) return null;
  const target: ArtworkTarget = {
    line,
    lineName: lineNames.get(line.line_no) ?? line.line_no,
    version,
  };
  const key = `${request.kind}-${request.versionId}`;
  switch (request.kind) {
    case "upload":
      return (
        <UploadFilesDialog
          key={`${key}-${request.replacing}`}
          target={target}
          replacing={request.replacing}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      );
    case "decide_proof":
      return (
        <DecideForCustomerDialog
          key={key}
          target={target}
          party={party}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      );
    case "reopen":
      return (
        <ReopenDialog
          key={key}
          target={target}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      );
    case "resend":
      return (
        <ResendProofDialog
          key={key}
          target={target}
          party={party}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      );
    case "check_warnings":
      return (
        <CheckWarningsDialog
          key={key}
          target={target}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      );
    case "accept":
      return (
        <AcceptForCustomerDialog
          key={key}
          target={target}
          party={party}
          busy={busy}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      );
  }
}
